import { useEffect } from 'react';
import { CmpEvent, ConsentDetails } from 'usercentrics';
import { handleUsercentricsConsent } from 'utils/usercentrics';
import useAnalyticsSocket from './useAnalyticsSocket';
import { events } from '@/constants/analytic-events';
import formatAndBuildAnalyticEvent from '@/utils/analytics';
import useAuthentication from './useAuthentication';

const listenedEvents: CmpEvent['type'][] = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE'];
const IMPLICIT_RETRY_COUNT = 2;
const RETRY_INTERVAL = 500;

type ConsentAnalyticsPayload = {
  consentDetails: ConsentDetails;
  eventName: string;
  citizenId?: string;
};

const useUsercentrics = () => {
  const { sendMessage } = useAnalyticsSocket();
  const { user } = useAuthentication();
  useEffect(() => {
    const citizenId = user?.id;
    const cmpEventListener = async (e: Event, retryCount = 0) => {
      const { detail: cmpEvent } = e as Event & { detail: CmpEvent };
      if (!listenedEvents.includes(cmpEvent.type)) return;
      setTimeout(async () => {
        const consentDetails = await window.__ucCmp?.getConsentDetails();
        if (!consentDetails) return;
        if (consentDetails.consent.type === 'IMPLICIT') {
          if (retryCount < IMPLICIT_RETRY_COUNT) return cmpEventListener(e, retryCount + 1);
          return;
        }

        handleUsercentricsConsent(consentDetails);

        const consentPayload = {
          eventName: events.USER_COOKIE_CONSENT,
          consentDetails,
          citizenId,
        } satisfies ConsentAnalyticsPayload;

        const socketEvent = formatAndBuildAnalyticEvent(consentPayload);
        sendMessage(socketEvent);
      }, RETRY_INTERVAL);
    };

    window.addEventListener('UC_UI_CMP_EVENT', cmpEventListener);

    return () => {
      window.removeEventListener('UC_UI_CMP_EVENT', cmpEventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendMessage]);
};

export default useUsercentrics;
